import React, { type ChangeEvent, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Lozenge from '@atlaskit/lozenge';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	type OnFetchParam,
	type ProjectPickerFilter,
	ProjectFilterChoice,
} from '../../../../common/ui/types';
import { messages } from './messages';

export type SearchWithFilterProps = {
	currentCountForAssociatedProjects: number;
	/**
	 * Prop was originally used to display the count of "total projects"
	 * which was deemed confusing, this UI component has been disabled
	 * we should clean up this state and component when we are sure there
	 * is no need for it.
	 */
	newCountForAssociatedProjects?: number;
	filter: ProjectPickerFilter;
	onFetch: (onFetchParam: OnFetchParam) => void;
};

export const SearchWithFilter = ({
	// Lozenge value for "Existing projects" dropdown item
	currentCountForAssociatedProjects,
	filter,
	onFetch,
}: SearchWithFilterProps) => {
	const { formatMessage } = useIntl();

	const { query, projectFilterChoice } = filter;

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const updatedQuery = event.target.value;
		onFetch({ query: updatedQuery });
	};

	const handleClick = (
		event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
	) => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const updatedProjectFilterChoice = (event.currentTarget as HTMLElement)
			.title as (typeof ProjectFilterChoice)[keyof typeof ProjectFilterChoice];

		if (projectFilterChoice !== updatedProjectFilterChoice) {
			onFetch({ projectFilterChoice: updatedProjectFilterChoice });
		}
	};

	return (
		<Container>
			<Textfield
				onChange={handleChange}
				elemBeforeInput={
					<SearchIcon label={formatMessage(messages.searchFieldSearchIconLabel)} size="small" />
				}
				elemAfterInput={
					query && (
						<button onClick={() => onFetch({ query: '' })}>
							<CrossCircleIcon
								label={formatMessage(messages.searchFieldClearSearchIconLabel)}
								size="small"
							/>
						</button>
					)
				}
				placeholder={formatMessage(messages.searchFieldPlaceholder)}
				value={query}
			/>
			<DropdownMenu trigger={projectFilterChoice}>
				<DropdownItemGroup>
					<Highlight selected={projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS}>
						<SelectedBorder selected={projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS} />
						<DropdownItem
							description={formatMessage(messages.allProjectsFilterDescription)}
							isSelected={projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS}
							onClick={handleClick}
							shouldDescriptionWrap
							title={ProjectFilterChoice.ALL_PROJECTS}
						>
							<Content>{formatMessage(messages.allProjectsFilter)}</Content>
						</DropdownItem>
					</Highlight>
					{currentCountForAssociatedProjects > 0 && (
						<Highlight selected={projectFilterChoice === ProjectFilterChoice.ASSOCIATED_PROJECTS}>
							<SelectedBorder
								selected={projectFilterChoice === ProjectFilterChoice.ASSOCIATED_PROJECTS}
							/>
							<DropdownItem
								description={formatMessage(messages.existingProjectsFilterDescription)}
								isSelected={projectFilterChoice === ProjectFilterChoice.ASSOCIATED_PROJECTS}
								onClick={handleClick}
								shouldDescriptionWrap
								title={ProjectFilterChoice.ASSOCIATED_PROJECTS}
							>
								<Content>
									{formatMessage(messages.existingProjectsFilter)}
									<Lozenge testId="admin-pages-priority-schemes.ui.priority-scheme-modal.project-picker.search-with-filter.existing-projects-count">
										{currentCountForAssociatedProjects}
									</Lozenge>
								</Content>
							</DropdownItem>
						</Highlight>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
		</Container>
	);
};

const layoutStyles = {
	display: 'flex',
	gap: `${token('space.100', '8px')}`,
	justifyContent: 'space-between',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...layoutStyles,
	height: `${token('space.400', '32px')}`,
	marginBottom: `${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > [data-ds--text-field--container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		button: {
			background: 'none',
			border: 'none',
		},
		paddingLeft: `${token('space.100', '8px')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-ds--text-field--container] + button': {
		minWidth: '128px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Content = styled.div(layoutStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Highlight = styled.div<{
	children?: ReactNode;
	selected: boolean;
}>({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'button[role="menuitem"]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ selected }) =>
			selected && `${token('color.background.accent.blue.subtlest', '#E9F2FF')}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: ({ selected }) => selected && `${token('color.text.selected', '#0C66E4')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedBorder = styled.span<{
	selected: boolean;
}>({
	backgroundColor: `${token('color.border.selected', '#0C66E4')}`,
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: ({ selected }) => (selected ? 'visible' : 'hidden'),
	width: `${token('space.025', '2px')}`,
});
